import {type ColorSchemeType} from 'platform/components';
import {match} from 'ts-pattern';

import i18n from '@dms/i18n';

import {buildArray, type Nullish} from 'shared/web';

import {
  type OfferAlternative,
  type OfferAlternativesCustomerChoice,
} from '../types/OfferAlternative';

const t = (key: string, fallback: string) => i18n.t(key, {defaultValue: fallback});

export type AlternativeFlag = {name: string; color: ColorSchemeType};

const getUnavailableFlag = (alternative: OfferAlternative): AlternativeFlag | null =>
  match(alternative.unavailableReason)
    .with('EXPIRED', () => ({
      name: t('entity.offerAlternative.flags.expired', 'Expired'),
      color: 'red' as const,
    }))
    .with('PRICE_EXPIRED', () => ({
      name: t('entity.offerAlternative.flags.priceExpired', 'Price expired'),
      color: 'red' as const,
    }))
    .with('PRICE_MISSING', () => ({
      name: t('entity.offerAlternative.flags.priceMissing', 'Price missing'),
      color: 'red' as const,
    }))
    .with('SOURCE_CHANGED', () => ({
      name: t('entity.offerAlternative.flags.sourceChanged', 'Source changed'),
      color: 'orange' as const,
    }))
    .with('SOURCE_UNAVAILABLE', 'OFFERED_ELSEWHERE', () => ({
      name: t('entity.offerAlternative.flags.unavailable', 'Unavailable'),
      color: 'red' as const,
    }))
    .with(null, () => null)
    .exhaustive();

/** Status flags of one alternative: kind, customer choice, applied, unavailability. */
export const getAlternativeFlags = (
  alternative: OfferAlternative,
  customerChoice: OfferAlternativesCustomerChoice | Nullish
) => {
  const isChosen = customerChoice?.alternativeId === alternative.id;

  return buildArray<AlternativeFlag>()
    .add({
      name:
        alternative.kind === 'CONFIGURATION'
          ? t('entity.offerAlternative.flags.configuration', 'Configuration')
          : t('entity.offerAlternative.flags.stock', 'Stock vehicle'),
      color: 'neutral',
    })
    .when(isChosen && customerChoice?.isCurrent === true, {
      name:
        customerChoice?.chosenBy === 'SELLER'
          ? t('entity.offerAlternative.flags.sellerSelected', 'Selected by seller')
          : t('entity.offerAlternative.flags.customerSelected', 'Customer selected'),
      color: 'green',
    })
    .when(isChosen && customerChoice?.isCurrent === false, {
      name: t('entity.offerAlternative.flags.confirmAgain', 'Confirm again'),
      color: 'orange',
    })
    .when(alternative.isApplied, {
      name: t('entity.offerAlternative.flags.applied', 'Applied'),
      color: 'blue',
    })
    .whenNotNil(getUnavailableFlag(alternative));
};
